import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {AppState} from '../store';
import {createSelector, select, Store} from '@ngrx/store';
import {selectPaypalConfig, selectTenantInfo} from '../store/selectors';
import {PaypalConfig} from '../models/paypal-config.model';
import {environment} from '../../environments/environment';
import {payPalScriptLoadRequested} from '../store/platform.actions';
import {checkIfPlatformSite} from '../common/platform-utils';
import {ActivatedRoute} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, first} from 'rxjs/operators';

const selectPaypalIntegrationData = createSelector(
  selectPaypalConfig,
  selectTenantInfo,
  (paypalConfig, tenant) => ({ paypalConfig, tenant })
);

@Component({
    selector: "paypal-integration",
    template: "",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaypalIntegrationComponent {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly route = inject(ActivatedRoute);

  constructor() {
    // Wait for the FIRST store emission where PayPal is fully configured, then load the SDK script.
    // NOTE: this is intentionally RxJS (filter + first) rather than a signal effect(). An effect()
    // only re-evaluates its guard if its tracked signals re-emit; the tenant's paypalConfig hydrates
    // on a later tick than this component mounts, and the effect was firing once with undefined data
    // and never again — so the SDK script was never injected and the PayPal button never rendered.
    // filter + first reliably waits until the config is present regardless of hydration ordering.
    this.store
      .pipe(
        select(selectPaypalIntegrationData),
        filter(data => !!data?.paypalConfig?.enabled && !!data?.paypalConfig?.paypalMerchantId),
        first(),
        takeUntilDestroyed(),
      )
      .subscribe(data => this.activatePaypalIntegration(data.paypalConfig, data?.tenant?.currency));
  }

  activatePaypalIntegration(config: PaypalConfig, currency: string) {
    if (!checkIfPlatformSite()) {
      let paypalPlatformClientId = environment.paypal.platformClientId;

      const testPaypalPlatformId = this.route.snapshot.queryParams['testPaypalPlatformId'];
      if (testPaypalPlatformId) {
        paypalPlatformClientId = testPaypalPlatformId;
      }

      var paypalScript = document.createElement('script');
      paypalScript.type = 'text/javascript';
      paypalScript.src = `https://www.paypal.com/sdk/js?client-id=${paypalPlatformClientId}&merchant-id=${config.paypalMerchantId}&currency=${currency}`;
      paypalScript.onerror = () => {
        console.error("PayPal script failed to load. Check integration parameters (e.g. Merchant Id) in Admin → Settings → Integrations.");
      };
      var head = document.getElementsByTagName('head')[0];
      head.appendChild(paypalScript);

      console.log("Activating Paypal integration");
      this.store.dispatch(payPalScriptLoadRequested());
    }
  }
}
